import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';

interface MaskedTimeInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  suffix?: React.ReactNode;
  className?: string;
}

const MaskedTimeInput: React.FC<MaskedTimeInputProps> = ({
                                                           value = '',
                                                           onChange,
                                                           onBlur,
                                                           onFocus,
                                                           placeholder,
                                                           suffix,
                                                           className
                                                         }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const formatTime = (input: string): string => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');

    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;

    // Format as HH:MM
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    // If user is deleting
    if (inputValue.length < localValue.length) {
      const formatted = formatTime(inputValue);
      setLocalValue(formatted);
      if (onChange) {
        onChange(formatted);
      }
      return;
    }

    // Format the input
    const formatted = formatTime(inputValue);

    // Limit to 5 characters (HH:MM)
    if (formatted.length <= 5) {
      setLocalValue(formatted);
      if (onChange) {
        onChange(formatted);
      }

      // Set cursor position after the formatting
      requestAnimationFrame(() => {
        if (inputRef.current && inputRef.current.input) {
          const input = inputRef.current.input;
          let newPos = cursorPos;

          // If we just added the colon, move cursor after it
          if (formatted.length === 3 && cursorPos === 2) {
            newPos = 3;
          } else if (cursorPos > localValue.length) {
            newPos = formatted.length;
          }

          input.setSelectionRange(newPos, newPos);
        }
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    // Allow control keys
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      (e.ctrlKey && (key === 'a' || key === 'c' || key === 'v' || key === 'x'))
    ) {
      return;
    }

    // Only allow digits
    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }

    // Don't allow more than 4 digits
    const currentDigits = localValue.replace(/\D/g, '');
    if (currentDigits.length >= 4 && cursorPos === selectionEnd) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 4);
    const formatted = formatTime(digits);

    setLocalValue(formatted);
    if (onChange) {
      onChange(formatted);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  const displayValue = isFocused || localValue ? localValue : '';
  const displayPlaceholder = isFocused && !localValue ? '__:__' : placeholder;

  return (
    <Input
      className={className}
      ref={inputRef}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={displayPlaceholder}
      suffix={suffix}
      maxLength={5}
    />
  );
};

export default MaskedTimeInput;
